import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { enrollmentStartSchema } from '@app/shared';
import type { EnrollmentStartInput } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { TextInput, applyServerErrors } from '../../components/form';
import { useStartEnrollment } from './api';
import { stepPath } from './labels';

/**
 * "Enrollment step 1" (PC1142): the ZIP code. The server checks it against the service area,
 * creates the Enrollment ID and decides the state, the city and the plans offered next.
 */
export function Step1Page() {
  const navigate = useNavigate();
  const notify = useNotify();
  const start = useStartEnrollment();
  // "Add Line" on a customer's page opens this step with the customer's ZIP already filled in.
  const [params] = useSearchParams();
  const { control, handleSubmit, setError } = useForm<EnrollmentStartInput>({
    resolver: zodResolver(enrollmentStartSchema),
    defaultValues: { zip: /^\d{5}$/.test(params.get('zip') ?? '') ? params.get('zip')! : '' },
  });

  const submit = handleSubmit(async (values) => {
    try {
      const enrollment = await start.mutateAsync(values);
      navigate(stepPath(enrollment.id, 2));
    } catch (err) {
      // The reason a ZIP cannot be used ("This ZIP code is inactive.") comes back on the field.
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <Box>
      <PageHeader title="Enrollment step 1" code="PC1142" />
      <Paper
        variant="outlined"
        component="form"
        noValidate
        onSubmit={(e) => void submit(e)}
        aria-label="Enrollment step 1"
        sx={{ p: 2, maxWidth: 420 }}
      >
        <Typography variant="h3" sx={{ mb: 2 }}>
          Step 1
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <TextInput
            control={control}
            name="zip"
            label="Enter zip code"
            required
            autoFocus
            autoComplete="off"
            slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
          />
          <Button type="submit" variant="contained" disabled={start.isPending} sx={{ mt: 0.25 }}>
            GO
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
